import api from './api';

/**
 * Currency Service
 * Centralized service for fetching and caching currency data
 */

// In-memory cache for currencies (loaded once per session)
let currencyCache = null;
let cachePromise = null;

/**
 * Get all currencies from the API
 * Results are cached for the session to avoid repeated API calls
 * @returns {Promise<object>} Currency data with major, all, byRegion
 */
export const getCurrencies = async () => {
  // Return cached data if available
  if (currencyCache) {
    return currencyCache;
  }

  // If a fetch is already in progress, wait for it
  if (cachePromise) {
    return cachePromise;
  }

  // Fetch and cache
  cachePromise = api.get('/currencies')
    .then(response => {
      currencyCache = response.data.data;
      cachePromise = null;
      return currencyCache;
    })
    .catch(error => {
      console.error('Failed to fetch currencies:', error);
      cachePromise = null;
      // Return fallback data if API fails
      return getFallbackCurrencies();
    });

  return cachePromise;
};

/**
 * Search currencies by code or name
 * @param {string} query - Search query
 * @returns {Promise<array>} Matching currencies
 */
export const searchCurrencies = async (query) => {
  const currencies = await getCurrencies();

  if (!query || query.length < 1) {
    return currencies.all;
  }

  const q = query.toLowerCase();
  return currencies.all.filter(c =>
    c.code.toLowerCase().includes(q) ||
    c.name.toLowerCase().includes(q) ||
    (c.region && c.region.toLowerCase().includes(q))
  );
};

/**
 * Get major currencies only
 * @returns {Promise<array>} Major currency objects
 */
export const getMajorCurrencies = async () => {
  const currencies = await getCurrencies();
  return currencies.major;
};

/**
 * Get currency by code
 * @param {string} code - Currency code (e.g., 'USD')
 * @returns {Promise<object|null>} Currency object or null
 */
export const getCurrency = async (code) => {
  const currencies = await getCurrencies();
  return currencies.all.find(c => c.code === code) || null;
};

/**
 * Get currency symbol by code
 * @param {string} code - Currency code
 * @returns {Promise<string>} Currency symbol or code if not found
 */
export const getCurrencySymbol = async (code) => {
  const currency = await getCurrency(code);
  return currency ? currency.symbol : code;
};

/**
 * Clear the currency cache (useful for testing or forced refresh)
 */
export const clearCache = () => {
  currencyCache = null;
  cachePromise = null;
};

/**
 * Fallback currencies if API is unavailable.
 *
 * Mirrors backend/src/config/currencies.js so users never see a truncated
 * dropdown when the /currencies endpoint times out or errors. If you add a
 * currency on the backend, add it here too — there's a unit-test style
 * reminder at the bottom of this file to catch drift early.
 */
const FALLBACK_MAJOR_CODES = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'NZD', 'SGD', 'HKD', 'CNY', 'ZAR'];

const FALLBACK_ALL_CURRENCIES = [
  // Major World Currencies
  { code: 'USD', name: 'US Dollar', symbol: '$', region: 'Americas' },
  { code: 'EUR', name: 'Euro', symbol: '€', region: 'Europe' },
  { code: 'GBP', name: 'British Pound', symbol: '£', region: 'Europe' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', region: 'Asia' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', region: 'Europe' },

  // Oceania
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', region: 'Oceania' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', region: 'Oceania' },

  // Americas
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', region: 'Americas' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', region: 'Americas' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', region: 'Americas' },
  { code: 'ARS', name: 'Argentine Peso', symbol: 'AR$', region: 'Americas' },
  { code: 'CLP', name: 'Chilean Peso', symbol: 'CL$', region: 'Americas' },
  { code: 'COP', name: 'Colombian Peso', symbol: 'CO$', region: 'Americas' },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/', region: 'Americas' },

  // Asia
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', region: 'Asia' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', region: 'Asia' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', region: 'Asia' },
  { code: 'TWD', name: 'Taiwan Dollar', symbol: 'NT$', region: 'Asia' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', region: 'Asia' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', region: 'Asia' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', region: 'Asia' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', region: 'Asia' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', region: 'Asia' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', region: 'Asia' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', region: 'Asia' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨', region: 'Asia' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', region: 'Asia' },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs', region: 'Asia' },

  // Europe
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', region: 'Europe' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', region: 'Europe' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', region: 'Europe' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', region: 'Europe' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', region: 'Europe' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', region: 'Europe' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei', region: 'Europe' },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв', region: 'Europe' },
  { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn', region: 'Europe' },
  { code: 'ISK', name: 'Icelandic Krona', symbol: 'kr', region: 'Europe' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', region: 'Europe' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', region: 'Europe' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', region: 'Europe' },

  // Africa
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', region: 'Africa' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', region: 'Africa' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', region: 'Africa' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', region: 'Africa' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.', region: 'Africa' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵', region: 'Africa' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', region: 'Africa' },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh', region: 'Africa' },

  // Middle East
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', region: 'Middle East' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', region: 'Middle East' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق', region: 'Middle East' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', region: 'Middle East' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب', region: 'Middle East' },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.', region: 'Middle East' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا', region: 'Middle East' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪', region: 'Middle East' }
];

const getFallbackCurrencies = () => {
  const major = FALLBACK_ALL_CURRENCIES.filter(c => FALLBACK_MAJOR_CODES.includes(c.code));
  const byRegion = {};
  for (const c of FALLBACK_ALL_CURRENCIES) {
    if (!byRegion[c.region]) byRegion[c.region] = [];
    byRegion[c.region].push(c);
  }
  return {
    baseCurrency: 'AUD',
    majorCodes: FALLBACK_MAJOR_CODES,
    major,
    all: FALLBACK_ALL_CURRENCIES,
    byRegion
  };
};

export default {
  getCurrencies,
  searchCurrencies,
  getMajorCurrencies,
  getCurrency,
  getCurrencySymbol,
  clearCache
};
